if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5;
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      
      // Wait for Swiper to be available
      this.waitForSwiper();
    }

    waitForSwiper() {
      const checkSwiper = () => {
        if (window.Swiper) {
          console.log('✅ Swiper found, initializing slider...');
          this.initSlider();
        } else {
          console.log('⏳ Waiting for Swiper...');
          setTimeout(checkSwiper, 100);
        }
      };
      checkSwiper();
    }

    initSlider() {
      if (!window.Swiper) {
        console.error('❌ Swiper library not found');
        return;
      }
      
      console.log('🎬 Initializing Swiper with config:', {
        autoplay: this.autoplay,
        speed: this.autoplaySpeed,
        showArrows: this.showArrows,
        showDots: this.showDots
      });

      const swiperConfig = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: false,
        speed: 1000,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        allowTouchMove: true,
        simulateTouch: true,
        touchRatio: 1,
        touchAngle: 45,
        shortSwipes: true,
        longSwipes: true,
        followFinger: true,
        threshold: 5,
        preventClicks: false,
        preventClicksPropagation: false,
        slideToClickedSlide: false,
        autoplay: this.autoplay ? {
          delay: this.autoplaySpeed * 1000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        } : false,
        on: {
          init: () => {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
          click: (swiper, event) => {
            console.log('👆 Swiper click detected, target:', event.target.className);
            
            // Check if click is on the media area (not on controls or buttons)
            const target = event.target;
            const clickedOnMedia = target.closest('.m-slide__media');
            const clickedOnButton = target.closest('.m-button, button, a.m-button');
            const clickedOnControls = target.closest('.m-slider-controls');
            
            console.log('🔍 Click analysis:', {
              onMedia: !!clickedOnMedia,
              onButton: !!clickedOnButton,
              onControls: !!clickedOnControls
            });
            
            // Only handle link navigation if clicked on media and not on buttons/controls
            if (clickedOnMedia && !clickedOnButton && !clickedOnControls) {
              const slide = target.closest('.swiper-slide');
              const link = slide ? slide.querySelector('.m-hidden-link') : null;
              
              if (link && link.href) {
                console.log('🖱️ Navigating to:', link.href);
                window.location.href = link.href;
              } else {
                console.log('ℹ️ No link configured for this slide');
              }
            }
          },
        },
      };

      console.log('⚙️ Swiper config:', {
        autoplay: swiperConfig.autoplay,
        delay: this.autoplay ? this.autoplaySpeed + 's' : 'disabled'
      });

      // Add navigation if enabled
      if (this.showArrows) {
        const nextEl = this.querySelector(".m-slider-controls__button-next");
        const prevEl = this.querySelector(".m-slider-controls__button-prev");
        console.log('🔍 Navigation elements:', { nextEl, prevEl });
        if (nextEl && prevEl) {
          swiperConfig.navigation = {
            nextEl: nextEl,
            prevEl: prevEl,
          };
          console.log('✅ Navigation configured');
        } else {
          console.warn('⚠️ Navigation elements not found');
        }
      }

      // Add pagination if enabled
      if (this.showDots) {
        const paginationEl = this.querySelector(".swiper-pagination");
        console.log('🔍 Pagination element:', paginationEl);
        if (paginationEl) {
          swiperConfig.pagination = {
            el: paginationEl,
            clickable: true,
            bulletClass: "m-dot",
            bulletActiveClass: "m-dot--active",
          };
          console.log('✅ Pagination configured');
        } else {
          console.warn('⚠️ Pagination element not found');
        }
      }

      try {
        this.slider = new window.Swiper(this.sliderContainer, swiperConfig);
        
        console.log('✅ Swiper instance created:', this.slider);
        console.log('📊 Slides count:', this.slider.slides?.length);
        
        // Check for image links
        const imageLinks = this.querySelectorAll('.m-hidden-link');
        console.log('🔗 Image links found:', imageLinks.length);
        imageLinks.forEach((link, index) => {
          console.log(`  Link ${index + 1}:`, link.href);
          
          // Add click handler to ensure links work
          link.addEventListener('click', (e) => {
            console.log('🖱️ Link clicked:', link.href);
            // Let the default behavior happen (navigation)
          });
        });
        
        // Handle video slides
        this.slider.on("slideChange", () => {
          console.log('🔄 Slide changed to:', this.slider.activeIndex);
          this.handleChange(this.slider);
        });

        // Handle Shopify theme editor
        if (Shopify.designMode) {
          console.log('🛠️ Shopify design mode detected');
          document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
        }

        console.log('🎉 Swiper initialized successfully');
      } catch (error) {
        console.error('❌ Error initializing Swiper:', error);
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            }).catch((error) => {
              console.log('Video play prevented:', error);
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      if (this.slider && typeof index === 'number') {
        this.slider.slideTo(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
